import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, Container, Grid, useTheme, useMediaQuery, InputAdornment, TextField, IconButton } from '@mui/material';
import { Icon } from '@iconify/react';
import TopBar from '../components/TopBar';
import Sidebar from '../components/Sidebar';
import SubAgentRecordsTable from '../components/SubAgentRecordsTable';
import SubAgentUserFormDrawer from '../components/SubAgentUserFormDrawer';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import PasswordResetHistoryDrawer from '../components/PasswordResetHistoryDrawer';
import { useLocation } from 'react-router-dom';
import { useToast } from '../components/ui/ToastProvider';
import { logUserCreation, logUserEdit, logStatusChange, logPasswordReset } from '../utils/auditLog';
import { generateSecureOTP, hashPassword } from '../utils/passwordPolicy';
import { sendNewUserOTPEmail, sendPasswordResetEmail } from '../utils/emailService';
import { createPasswordResetHistory } from '../utils/passwordResetHistory';
import { shouldShowResendEmail, logEmailResend } from '../utils/passwordResetHistory';
interface SubAgentUsersProps {
  onLogout: () => void;
}
const STORAGE_KEY = 'sub_agent_users';
const SUB_AGENTS = ['Islamabad Exchange', 'Karachi Money Transfer', 'Lahore Financial', 'Peshawar Remit', 'Quetta Exchange'];
const BRANCH_CODES = ['ISB001', 'ISB002', 'KHI001', 'KHI002', 'LHR001', 'LHR002', 'PSH001', 'QTA001'];
// Initial mock data
const INITIAL_MOCK_USERS = Array(30).fill(null).map((_, index) => ({
  id: index + 1,
  name: ['Mohammed Ali', 'Sara Khan', 'Ahmed Hassan', 'Fatima Zaidi', 'Khalid Mahmood'][index % 5],
  fatherName: ['Ali Ahmed', 'Khan Mohammed', 'Hassan Ibrahim', 'Zaidi Tariq', 'Mahmood Khalid'][index % 5],
  cnic: index % 3 === 0 ? `${10000 + index * 3}-${1234567}-${index % 10}` : '',
  email: `user${index + 1}@${['islamabad', 'karachi', 'lahore', 'peshawar', 'quetta'][index % 5]}exchange.pk`,
  phone: `+92 ${300 + index % 100} ${1000000 + index}`,
  username: ['mohammed.ali', 'sara.khan', 'ahmed.hassan', 'fatima.zaidi', 'khalid.mahmood'][index % 5] + index,
  branchCode: BRANCH_CODES[index % BRANCH_CODES.length],
  settlementAccount: `BIPL-BR-${10000000 + index * 7}`,
  subAgent: SUB_AGENTS[index % SUB_AGENTS.length],
  status: index % 7 === 0 ? 'inactive' : 'active',
  userType: index % 3 === 0 ? 'checker' : 'maker',
  createdAt: new Date(Date.now() - Math.floor(Math.random() * 10000000000)).toISOString()
}));
const SubAgentUsers: React.FC<SubAgentUsersProps> = ({
  onLogout
}) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userDrawerOpen, setUserDrawerOpen] = useState(false);
  const [editUser, setEditUser] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [subAgentFilter, setSubAgentFilter] = useState<string | null>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [selectedUserForReset, setSelectedUserForReset] = useState<any>(null);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [resetPasswordError, setResetPasswordError] = useState<string | undefined>();
  const [resetHistoryDrawerOpen, setResetHistoryDrawerOpen] = useState(false);
  const [selectedUserForResetHistory, setSelectedUserForResetHistory] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const {
    showToast
  } = useToast();
  // Load users from localStorage on mount
  useEffect(() => {
    const savedUsers = localStorage.getItem(STORAGE_KEY);
    if (savedUsers) {
      try {
        const parsedUsers = JSON.parse(savedUsers);
        setUsers(parsedUsers);
      } catch (error) {
        console.error('Error loading sub agent users from localStorage:', error);
        setUsers(INITIAL_MOCK_USERS);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(INITIAL_MOCK_USERS));
      }
    } else {
      // First time load - use mock data
      setUsers(INITIAL_MOCK_USERS);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(INITIAL_MOCK_USERS));
    }
  }, []);
  // Save users to localStorage whenever they change
  useEffect(() => {
    // Always save to localStorage, even if empty
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(users));
    } catch (error) {
      console.error('Error saving users to localStorage:', error);
      showToast('Failed to save user data', 'error');
    }
  }, [users]);
  useEffect(() => {
    // Check for subAgent filter in URL
    const params = new URLSearchParams(location.search);
    const subAgentParam = params.get('subAgent');
    if (subAgentParam) {
      setSubAgentFilter(subAgentParam);
    }
  }, [location]);
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  const handleAddUser = () => {
    setEditUser(null);
    setUserDrawerOpen(true);
  };
  const handleEditUser = (user: any) => {
    setEditUser(user);
    setUserDrawerOpen(true);
  };
  const handleCloseUserDrawer = () => {
    setUserDrawerOpen(false);
    setEditUser(null);
  };
  const handleSaveUser = async (userData: any) => {
    if (!userData.id) {
      // New user creation
      const newOTP = generateSecureOTP();
      const hashedOTP = hashPassword(newOTP);
      const newUser = {
        ...userData,
        id: users.length > 0 ? Math.max(...users.map(u => u.id)) + 1 : 1,
        status: 'active' as const,
        createdAt: new Date().toISOString(),
        hashedPassword: hashedOTP,
        firstLoginRequired: true,
        passwordResetRequired: true
      };
      const updatedUsers = [newUser, ...users];
      setUsers(updatedUsers);
      logUserCreation(newUser.id.toString(), newUser.name, newUser);
      try {
        showToast('Sending OTP email...', 'info', 'Creating User', 2000);
        const emailResult = await sendNewUserOTPEmail({
          recipientEmail: newUser.email,
          recipientName: newUser.name,
          otp: newOTP,
          expiryMinutes: 24
        });
        createPasswordResetHistory(newUser.id.toString(), newUser.name, 'SYSTEM', newUser.email, emailResult.success ? 'SUCCESS' : 'FAILED', {
          resetByName: 'System (User Creation)',
          reason: 'New user account created',
          deliveryError: emailResult.error,
          emailMessageId: emailResult.messageId
        });
        if (emailResult.success) {
          setUserDrawerOpen(false);
          showToast(`User created successfully. Temporary password sent to ${newUser.email}`, 'success', 'User Created', 6000);
        } else {
          setUserDrawerOpen(false);
          showToast(`User created but email delivery failed: ${emailResult.error}. Temporary password: ${newOTP}`, 'error', 'Email Delivery Failed', 10000);
        }
      } catch (error) {
        createPasswordResetHistory(newUser.id.toString(), newUser.name, 'SYSTEM', newUser.email, 'FAILED', {
          resetByName: 'System (User Creation)',
          reason: 'New user account created',
          deliveryError: 'Email service error'
        });
        setUserDrawerOpen(false);
        showToast(`User created but email failed to send. Temporary password: ${newOTP}`, 'error', 'Email Service Error', 10000);
      }
    } else {
      // User update
      const oldUser = users.find(u => u.id === userData.id);
      const updatedUsers = users.map(u => u.id === userData.id ? {
        ...userData,
        createdAt: u.createdAt
      } : u);
      setUsers(updatedUsers);
      // Log user edit in audit log
      if (oldUser) {
        logUserEdit(userData.id.toString(), userData.name, oldUser, userData);
      }
      setUserDrawerOpen(false);
      showToast('User updated successfully', 'success');
    }
  };
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };
  const clearSearch = () => {
    setSearchQuery('');
  };
  const handleResetPassword = (user: any) => {
    setSelectedUserForReset(user);
    setResetPasswordDialogOpen(true);
    setResetPasswordError(undefined);
  };
  const confirmResetPassword = async (reason?: string) => {
    if (!selectedUserForReset) return;
    setIsResettingPassword(true);
    setResetPasswordError(undefined);
    try {
      const newOTP = generateSecureOTP();
      const hashedOTP = hashPassword(newOTP);
      const updatedUsers = users.map(u => u.id === selectedUserForReset.id ? {
        ...u,
        hashedPassword: hashedOTP,
        passwordResetRequired: true,
        lastPasswordReset: new Date().toISOString()
      } : u);
      setUsers(updatedUsers);
      logPasswordReset(selectedUserForReset.id.toString(), selectedUserForReset.name, reason);
      const emailResult = await sendPasswordResetEmail({
        recipientEmail: selectedUserForReset.email,
        recipientName: selectedUserForReset.name,
        otp: newOTP,
        resetBy: 'BO Admin',
        reason: reason
      });
      createPasswordResetHistory(selectedUserForReset.id.toString(), selectedUserForReset.name, 'BO_ADMIN', selectedUserForReset.email, emailResult.success ? 'SUCCESS' : 'FAILED', {
        resetByName: 'Admin User',
        reason: reason || 'Password reset requested by admin',
        deliveryError: emailResult.error,
        emailMessageId: emailResult.messageId
      });
      setResetPasswordDialogOpen(false);
      setIsResettingPassword(false);
      setSelectedUserForReset(null);
      if (emailResult.success) {
        showToast(`Password reset successful. New temporary password sent to ${selectedUserForReset.email}`, 'success', 'Password Reset', 6000);
      } else {
        showToast(`Password reset successful but email delivery failed: ${emailResult.error}. New temporary password: ${newOTP}`, 'warning', 'Password Reset - Email Failed', 10000);
      }
    } catch (error) {
      setResetPasswordError('Failed to reset password. Please try again.');
      setIsResettingPassword(false);
    }
  };
  const handleViewResetHistory = (user: any) => {
    setSelectedUserForResetHistory({
      id: user.id.toString(),
      name: user.name
    });
    setResetHistoryDrawerOpen(true);
  };
  const handleResendEmail = async (user: any) => {
    try {
      showToast('Sending OTP email...', 'info', 'Resending Email', 2000);
      // Generate new secure OTP
      const newOTP = generateSecureOTP();
      const hashedOTP = hashPassword(newOTP);
      // Update user with new hashed password
      const updatedUsers = users.map(u => u.id === user.id ? {
        ...u,
        hashedPassword: hashedOTP,
        passwordResetRequired: true,
        lastPasswordReset: new Date().toISOString()
      } : u);
      setUsers(updatedUsers);
      // Send OTP email
      const emailResult = await sendPasswordResetEmail({
        recipientEmail: user.email,
        recipientName: user.name,
        otp: newOTP,
        resetBy: 'BO Admin',
        reason: 'Email resend requested by admin'
      });
      // Create password reset history entry
      createPasswordResetHistory(user.id.toString(), user.name, 'BO_ADMIN', user.email, emailResult.success ? 'SUCCESS' : 'FAILED', {
        resetByName: 'Admin User',
        reason: 'Email resend - OTP regenerated',
        deliveryError: emailResult.error,
        emailMessageId: emailResult.messageId
      });
      // Log email resend in audit log
      logEmailResend(user.id.toString(), user.name, user.email, emailResult.success, 'Email resend requested by admin');
      // Show appropriate toast based on email delivery
      if (emailResult.success) {
        showToast(`Email sent successfully to ${user.email}`, 'success', 'Email Sent', 6000);
      } else {
        showToast(`Email sending failed: ${emailResult.error}. New temporary password: ${newOTP}`, 'error', 'Email Failed', 10000);
      }
    } catch (error) {
      showToast('Failed to resend email. Please try again.', 'error', 'Error', 6000);
    }
  };
  return <Box sx={{
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: '#F8FAFC'
  }} data-id="element-4420" data-mp="1o5mx4smi8hlz">
      <TopBar onMenuClick={handleDrawerToggle} onLogout={onLogout} data-id="element-4421" data-mp="1s5dj8j1369k6" />
      <Sidebar open={mobileOpen} onClose={handleDrawerToggle} activePage="sub-agent-users" data-id="element-4422" data-mp="1e6dulbdkpuo0" />
      <Box component="main" sx={{
      flexGrow: 1,
      p: 3,
      width: {
        xs: '100%',
        sm: `calc(100% - ${240}px)`
      },
      ml: {
        xs: 0,
        sm: `${240}px`
      },
      mt: 8
    }} data-id="element-4423" data-mp="1j4yxjik7msg5">
        <Container maxWidth="xl" data-id="element-4424" data-mp="2zj75a70bmx7q">
          {/* Header */}
          <Grid container spacing={2} alignItems="center" sx={{
          mb: 3
        }} data-id="element-4425" data-mp="33lvieqw2iqai">
            <Grid item xs={12} md={6} data-id="element-4426" data-mp="29x63ej5vvu7f">
              <Box sx={{
              display: 'flex',
              alignItems: 'center'
            }} data-id="element-4427" data-mp="29y7e2mizmeom">
                <Icon icon="lucide:user" width={24} height={24} color="#0EA5E9" data-id="element-4428" data-mp="1zz050bjad67b" />
                <Typography variant="h5" component="h1" sx={{
                ml: 1.5,
                fontWeight: 600
              }} data-id="element-4429" data-mp="1vypmjzeb4nbk">
                  Sub Agent Users
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6} data-id="element-4430" data-mp="1h6ntozanmul1">
              <Box sx={{
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap',
              justifyContent: {
                xs: 'flex-start',
                md: 'flex-end'
              }
            }} data-id="element-4431" data-mp="1c9nnmodt1rtx">
                <Button variant="contained" startIcon={<Icon icon="lucide:plus" width={18} height={18} data-id="element-4433" data-mp="3sw3scipbdpn9" />} onClick={handleAddUser} data-id="element-4432" data-mp="3nvps3mtyfjzs">
                  Add User
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} data-id="element-4434" data-mp="307y61rim3slb">
              <TextField fullWidth placeholder="Search users by name, email, CNIC, or phone..." variant="outlined" value={searchQuery} onChange={handleSearch} InputProps={{
              startAdornment: <InputAdornment position="start" data-id="element-4436" data-mp="3exdwf6e8dw0h">
                      <Icon icon="lucide:search" width={20} height={20} color="#475569" data-id="element-4437" data-mp="2hhgp0k756lfs" />
                    </InputAdornment>,
              endAdornment: searchQuery ? <InputAdornment position="end" data-id="element-4438" data-mp="2acfiy9dbtyzn">
                      <IconButton size="small" onClick={clearSearch} data-id="element-4439" data-mp="2o4vs7rwtmt90">
                        <Icon icon="lucide:x" width={18} height={18} data-id="element-4440" data-mp="2o43vgdrhmph5" />
                      </IconButton>
                    </InputAdornment> : null
            }} data-id="element-4435" data-mp="308rc0q9qye2e" />
            </Grid>
          </Grid>
          {/* Sub Agent Users Table */}
          <SubAgentRecordsTable records={users} onEditRecord={handleEditUser} searchQuery={searchQuery} subAgentFilter={subAgentFilter} onStatusChange={(user, newStatus) => {
          const updatedUsers = users.map(u => u.id === user.id ? {
            ...u,
            status: newStatus
          } : u);
          setUsers(updatedUsers);
          logStatusChange(user.id.toString(), user.name, user.status, newStatus);
          showToast(`User ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`, 'success');
        }} onResetPassword={handleResetPassword} onViewResetHistory={handleViewResetHistory} onResendEmail={handleResendEmail} data-id="element-4441" data-mp="1rfo3kb0wo40v" />
          {/* Sub Agent User Form Drawer */}
          <SubAgentUserFormDrawer open={userDrawerOpen} user={editUser} onClose={handleCloseUserDrawer} onSave={handleSaveUser} preselectedSubAgent={subAgentFilter} data-id="element-4442" data-mp="17l4ov0tmieqt" />
          {/* Reset Password Confirmation Dialog */}
          {selectedUserForReset && <ConfirmDialog open={resetPasswordDialogOpen} title="Reset Password" message={`Are you sure you want to reset the password for ${selectedUserForReset.name}? A new temporary password will be generated and sent to ${selectedUserForReset.email}.`} confirmLabel="Reset Password" showReason={true} reasonLabel="Reason for reset (optional)" reasonPlaceholder="Enter reason for password reset..." onConfirm={confirmResetPassword} onCancel={() => {
          setResetPasswordDialogOpen(false);
          setSelectedUserForReset(null);
        }} isLoading={isResettingPassword} error={resetPasswordError} data-id="element-4443" data-mp="251butyztp4st" />}
          <PasswordResetHistoryDrawer open={resetHistoryDrawerOpen} onClose={() => setResetHistoryDrawerOpen(false)} userId={selectedUserForResetHistory?.id} userName={selectedUserForResetHistory?.name} data-id="element-4444" data-mp="9w2fzr878km7" />
        </Container>
      </Box>
    </Box>;
};
export default SubAgentUsers;